import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Button,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { usePromotions } from "../../context/promotionsContext/promotionsContext";

const BANNER_STATE_KEY = "parishAudioFlashSaleBannerState";

const BANNER_STATES = {
  EXPANDED: "expanded",
  COLLAPSED: "collapsed",
};

function getInitialBannerState() {
  if (typeof window === "undefined") {
    return BANNER_STATES.EXPANDED;
  }

  try {
    const storedState = window.sessionStorage.getItem(BANNER_STATE_KEY);

    if (storedState === BANNER_STATES.COLLAPSED) {
      return BANNER_STATES.COLLAPSED;
    }
  } catch {
    // sessionStorage may be unavailable in some privacy modes.
  }

  return BANNER_STATES.EXPANDED;
}

function saveBannerState(nextState) {
  if (typeof window === "undefined") {
    return;
  }

  try {
    if (nextState === BANNER_STATES.EXPANDED) {
      window.sessionStorage.removeItem(BANNER_STATE_KEY);
    } else {
      window.sessionStorage.setItem(BANNER_STATE_KEY, nextState);
    }
  } catch {
    // sessionStorage may be unavailable in some privacy modes.
  }
}

export default function FlashSaleBanner() {
  const { activePromotions } = usePromotions();

  const prefersReducedMotion = useMediaQuery(
    "(prefers-reduced-motion: reduce)"
  );

  const contentRef = useRef(null);
  const animationFrameRef = useRef(null);

  const salePromotion = useMemo(() => {
    return activePromotions.find(
      (promotion) =>
        promotion?.promoType === "percentage_off" &&
        Number(promotion?.discountPercent) === 50 &&
        promotion?.scopeType === "global"
    );
  }, [activePromotions]);

  const [displayMode, setDisplayMode] = useState(
    getInitialBannerState
  );

  const [contentHeight, setContentHeight] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);

  /*
   * Measure the currently rendered banner and animate the outer
   * wrapper directly to that height.
   *
   * This avoids collapsing to zero and reopening, which caused
   * the previous overshoot/bounce effect.
   */
  useLayoutEffect(() => {
    const contentElement = contentRef.current;

    if (!contentElement) {
      return undefined;
    }

    const updateHeight = () => {
      if (animationFrameRef.current) {
        window.cancelAnimationFrame(animationFrameRef.current);
      }

      animationFrameRef.current = window.requestAnimationFrame(() => {
        const nextHeight = Math.ceil(
          contentElement.getBoundingClientRect().height
        );

        setContentHeight(nextHeight);
        animationFrameRef.current = null;
      });
    };

    updateHeight();

    let resizeObserver;

    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(updateHeight);
      resizeObserver.observe(contentElement);
    }

    window.addEventListener("resize", updateHeight);

    return () => {
      if (animationFrameRef.current) {
        window.cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }

      resizeObserver?.disconnect();
      window.removeEventListener("resize", updateHeight);
    };
  }, [displayMode]);

  useEffect(() => {
    if (!salePromotion?.endDate) {
      setTimeLeft(null);
      return undefined;
    }

    const updateTimer = () => {
      const now = Date.now();
      const end = new Date(salePromotion.endDate).getTime();

      if (!Number.isFinite(end)) {
        setTimeLeft(null);
        return;
      }

      const distance = end - now;

      if (distance <= 0) {
        setTimeLeft(null);
        return;
      }

      setTimeLeft({
        days: Math.floor(distance / (1000 * 60 * 60 * 24)),
        hours: Math.floor(
          (distance / (1000 * 60 * 60)) % 24
        ),
        minutes: Math.floor(
          (distance / (1000 * 60)) % 60
        ),
        seconds: Math.floor((distance / 1000) % 60),
      });
    };

    updateTimer();

    const interval = window.setInterval(updateTimer, 1000);

    return () => window.clearInterval(interval);
  }, [salePromotion]);

  const changeDisplayMode = (nextMode) => {
    if (nextMode === displayMode) {
      return;
    }

    setDisplayMode(nextMode);
    saveBannerState(nextMode);
  };

  if (!salePromotion) {
    return null;
  }

  const isExpanded = displayMode === BANNER_STATES.EXPANDED;

  return (
    <Box
      sx={{
        width: "100%",
        height:
          contentHeight === null
            ? "auto"
            : `${contentHeight}px`,
        overflow: "hidden",

        /*
         * This curve is a smooth ease-out with no overshoot.
         */
        transition: prefersReducedMotion
          ? "none"
          : "height 320ms cubic-bezier(0.22, 1, 0.36, 1)",

        willChange: prefersReducedMotion ? "auto" : "height",
      }}
    >
      <Box
        ref={contentRef}
        key={displayMode}
        sx={{
          width: "100%",

          animation: prefersReducedMotion
            ? "none"
            : "flashBannerContentIn 220ms cubic-bezier(0.22, 1, 0.36, 1)",

          "@keyframes flashBannerContentIn": {
            from: {
              opacity: 0.82,
              transform: isExpanded
                ? "translateY(-3px)"
                : "translateY(3px)",
            },
            to: {
              opacity: 1,
              transform: "translateY(0)",
            },
          },
        }}
      >
        {isExpanded ? (
          <ExpandedSaleBanner
            timeLeft={timeLeft}
            onCollapse={() =>
              changeDisplayMode(BANNER_STATES.COLLAPSED)
            }
          />
        ) : (
          <CollapsedSaleBanner
            timeLeft={timeLeft}
            onExpand={() =>
              changeDisplayMode(BANNER_STATES.EXPANDED)
            }
          />
        )}
      </Box>
    </Box>
  );
}

function ExpandedSaleBanner({ timeLeft, onCollapse }) {
  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        minHeight: 200,
        borderRadius: "18px",
        overflow: "hidden",
        border: "1px solid rgba(255,255,255,0.2)",
        backgroundImage: `
          linear-gradient(
            180deg,
            rgba(4, 7, 12, 0.10) 0%,
            rgba(4, 7, 12, 0.12) 45%,
            rgba(4, 7, 12, 0.26) 100%
          ),
          linear-gradient(
            90deg,
            rgba(5, 8, 14, 0.78) 0%,
            rgba(5, 8, 14, 0.42) 52%,
            rgba(5, 8, 14, 0.16) 100%
          ),
          url("/assets/plugins-collage-banner.png")
        `,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        boxShadow: "0 10px 28px rgba(0,0,0,0.28)",
        isolation: "isolate",
      }}
    >
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          px: { xs: 2, sm: 2.5, md: 3.25 },
          pt: { xs: 2, sm: 2.2, md: 2.1 },

          /*
           * Leaves room for the chevron in the lower-right.
           */
          pb: { xs: 5.75, sm: 5.5, md: 4.75 },
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={{ xs: 1.5, md: 1.5 }}
          alignItems={{ xs: "stretch", md: "center" }}
          justifyContent="space-between"
        >
          <Box
            sx={{
              minWidth: 0,
              textAlign: { xs: "center", md: "left" },
            }}
          >
            <Typography
              sx={{
                color: "#ffcf63",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.22em",
                fontSize: { xs: "0.82rem", md: "0.9rem" },
                lineHeight: 1.1,
                mb: 0.75,
              }}
            >
              Parish Audio
            </Typography>

            <Box
              sx={{
                width: 90,
                height: 4,
                borderRadius: "999px",
                background:
                  "linear-gradient(90deg, #ff9d2c 0%, #ffcf63 100%)",
                boxShadow:
                  "0 0 20px rgba(255, 157, 44, 0.28)",
                mb: 1.6,
                mx: { xs: "auto", md: 0 },
              }}
            />

            <Typography
              sx={{
                color: "#f4f6f8",
                fontWeight: 800,
                lineHeight: 0.95,
                textTransform: "uppercase",
                letterSpacing: "-0.03em",
                fontSize: {
                  xs: "2.1rem",
                  sm: "2.6rem",
                  md: "3rem",
                },
                textShadow:
                  "0 10px 24px rgba(0, 0, 0, 0.35)",
              }}
            >
              Summer Sale
            </Typography>

            <Typography
              sx={{
                mt: 0.4,
                color: "#ffcf63",
                fontWeight: 900,
                lineHeight: 0.9,
                letterSpacing: "-0.05em",
                fontSize: {
                  xs: "2.2rem",
                  sm: "3rem",
                  md: "3.75rem",
                },
                textTransform: "uppercase",
                textShadow:
                  "0 0 28px rgba(255, 157, 44, 0.22), 0 12px 30px rgba(0, 0, 0, 0.36)",
              }}
            >
              50% OFF
            </Typography>

            <Typography
              sx={{
                mt: 0.35,
                color: "#f4f6f8",
                fontWeight: 700,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                fontSize: {
                  xs: "1rem",
                  sm: "1.1rem",
                  md: "1.2rem",
                },
              }}
            >
              Every Plugin
            </Typography>

            {!timeLeft && (
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  minHeight: 48,
                  px: 2.1,
                  mt: 1.6,
                  borderRadius: "14px",
                  background: "rgba(11, 16, 24, 0.78)",
                  border:
                    "1px solid rgba(255, 255, 255, 0.08)",
                  backdropFilter: "blur(8px)",
                  boxShadow:
                    "0 8px 24px rgba(0, 0, 0, 0.25)",
                }}
              >
                <Typography
                  sx={{
                    color: "#c9ced6",
                    fontWeight: 600,
                    fontSize: {
                      xs: "1rem",
                      md: "1.05rem",
                    },
                  }}
                >
                  Through July 5
                </Typography>
              </Box>
            )}
          </Box>

          <Stack
            spacing={1.25}
            alignItems="center"
            justifyContent="center"
            sx={{
              flexShrink: 0,
              width: { xs: "100%", md: "auto" },
            }}
          >
            {timeLeft && (
              <Stack
                spacing={0.8}
                alignItems="center"
                justifyContent="center"
              >
                <Typography
                  sx={{
                    color: "#ffcf63",
                    fontWeight: 900,
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    fontSize: {
                      xs: "0.72rem",
                      md: "0.8rem",
                    },
                    whiteSpace: "nowrap",
                  }}
                >
                  Sale Ends In
                </Typography>

                <Stack
                  direction="row"
                  spacing={0.75}
                  justifyContent="center"
                >
                  <CountdownBox
                    label="days"
                    value={timeLeft.days}
                  />

                  <CountdownBox
                    label="hrs"
                    value={timeLeft.hours}
                  />

                  <CountdownBox
                    label="min"
                    value={timeLeft.minutes}
                  />

                  <CountdownBox
                    label="sec"
                    value={timeLeft.seconds}
                  />
                </Stack>
              </Stack>
            )}

            <SaleButton />
          </Stack>
        </Stack>
      </Box>

      <BannerChevronButton
        direction="up"
        onClick={onCollapse}
      />
    </Box>
  );
}

function CollapsedSaleBanner({ timeLeft, onExpand }) {
  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        minHeight: 48,
        borderRadius: "10px",
        overflow: "hidden",
        border: "1px solid rgba(255,255,255,0.16)",
        background:
          "linear-gradient(90deg, rgba(13,18,27,0.98) 0%, rgba(27,19,11,0.98) 100%)",
        boxShadow: "0 5px 16px rgba(0,0,0,0.22)",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        sx={{
          minHeight: 48,
          pl: { xs: 1.25, sm: 1.75 },

          /*
           * Leaves room for the lower-right expand chevron.
           */
          pr: { xs: 6.5, sm: 7 },
          py: 0.6,
        }}
      >
        <Typography
          sx={{
            minWidth: 0,
            color: "#f4f6f8",
            fontWeight: 700,
            fontSize: {
              xs: "0.76rem",
              sm: "0.9rem",
            },
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          <Box
            component="span"
            sx={{
              color: "#ffcf63",
              fontWeight: 900,
            }}
          >
            Summer Sale: 50% off every plugin
          </Box>

          <Box
            component="span"
            sx={{
              display: { xs: "none", md: "inline" },
              color: "#c9ced6",
            }}
          >
            {" "}
            · {getCompactTimeText(timeLeft)}
          </Box>
        </Typography>

        <Button
          component={RouterLink}
          to="/products"
          sx={{
            minHeight: 32,
            px: { xs: 1.1, sm: 1.6 },
            borderRadius: "8px",
            color: "#181109",
            background:
              "linear-gradient(180deg, #ffcf63 0%, #ff9d2c 100%)",
            fontWeight: 900,
            fontSize: {
              xs: "0.68rem",
              sm: "0.76rem",
            },
            lineHeight: 1,
            whiteSpace: "nowrap",
            textTransform: "uppercase",
            flexShrink: 0,
            "&:hover": {
              background:
                "linear-gradient(180deg, #ffd978 0%, #ffab3d 100%)",
            },
          }}
        >
          Shop Sale
        </Button>
      </Stack>

      <BannerChevronButton
        direction="down"
        onClick={onExpand}
      />
    </Box>
  );
}

function BannerChevronButton({ direction, onClick }) {
  const isUp = direction === "up";

  return (
    <Button
      type="button"
      aria-label={
        isUp
          ? "Collapse sale banner"
          : "Expand sale banner"
      }
      title={
        isUp
          ? "Collapse banner"
          : "Expand banner"
      }
      onClick={onClick}
      sx={{
        position: "absolute",
        right: 10,
        bottom: 8,
        zIndex: 3,

        minWidth: 34,
        width: 34,
        height: 34,
        p: 0,

        borderRadius: "8px",
        color: "#f4f6f8",
        backgroundColor: "rgba(5, 8, 14, 0.76)",
        border: "1px solid rgba(255,255,255,0.14)",
        backdropFilter: "blur(8px)",

        transition:
          "background-color 160ms ease, transform 160ms ease",

        "&:hover": {
          backgroundColor: "rgba(18, 24, 34, 0.94)",
          transform: "translateY(-1px)",
        },

        "&:focus-visible": {
          outline: "2px solid #ffcf63",
          outlineOffset: "2px",
        },
      }}
    >
      <ChevronIcon direction={direction} />
    </Button>
  );
}

function ChevronIcon({ direction }) {
  const isUp = direction === "up";

  return (
    <Box
      component="span"
      aria-hidden="true"
      sx={{
        display: "block",
        width: 9,
        height: 9,

        borderStyle: "solid",
        borderColor: "currentColor",
        borderWidth: "0 2px 2px 0",

        transform: isUp
          ? "translateY(2px) rotate(-135deg)"
          : "translateY(-2px) rotate(45deg)",

        transformOrigin: "center",
      }}
    />
  );
}

function SaleButton() {
  return (
    <Button
      component={RouterLink}
      to="/products"
      sx={{
        borderRadius: "14px",
        px: 2.7,
        py: 1.1,
        textTransform: "uppercase",
        fontWeight: 800,
        letterSpacing: "0.04em",
        color: "#181109",
        background:
          "linear-gradient(180deg, #ffcf63 0%, #ff9d2c 100%)",
        border: "none",
        minWidth: { xs: 220, sm: 220 },
        boxShadow:
          "0 12px 30px rgba(0, 0, 0, 0.30), 0 0 22px rgba(255, 157, 44, 0.18)",

        transition:
          "background 160ms ease, transform 160ms ease",

        "&:hover": {
          background:
            "linear-gradient(180deg, #ffd978 0%, #ffab3d 100%)",
          transform: "translateY(-1px)",
        },
      }}
    >
      Shop the Sale
    </Button>
  );
}

function CountdownBox({ value, label }) {
  const displayValue = String(value).padStart(2, "0");

  return (
    <Box
      sx={{
        minWidth: { xs: 54, sm: 60 },
        px: 0.8,
        py: 0.65,
        borderRadius: "10px",
        background:
          "linear-gradient(180deg, #fff7df 0%, #ffd166 100%)",
        border: "1px solid rgba(255,255,255,0.35)",
        boxShadow: "0 5px 14px rgba(0,0,0,0.22)",
        textAlign: "center",
      }}
    >
      <Typography
        sx={{
          color: "#111",
          fontWeight: 900,
          fontSize: {
            xs: "1rem",
            sm: "1.15rem",
          },
          lineHeight: 1,
        }}
      >
        {displayValue}
      </Typography>

      <Typography
        sx={{
          color: "rgba(0,0,0,0.68)",
          fontWeight: 800,
          fontSize: "0.68rem",
          lineHeight: 1.15,
          mt: 0.35,
          textTransform: "uppercase",
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

function getCompactTimeText(timeLeft) {
  if (!timeLeft) {
    return "Through July 5";
  }

  if (timeLeft.days > 0) {
    return `Ends in ${timeLeft.days}d ${timeLeft.hours}h`;
  }

  if (timeLeft.hours > 0) {
    return `Ends in ${timeLeft.hours}h ${timeLeft.minutes}m`;
  }

  if (timeLeft.minutes > 0) {
    return `Ends in ${timeLeft.minutes}m`;
  }

  return "Ending soon";
}
// import React, { useEffect, useMemo, useState } from "react";
// import { Box, Button, Stack, Typography } from "@mui/material";
// import { Link as RouterLink } from "react-router-dom";
// import { usePromotions } from "../../context/promotionsContext/promotionsContext";

// export default function FlashSaleBanner() {
//   const { activePromotions } = usePromotions();

//   const salePromotion = useMemo(() => {
//     return activePromotions.find(
//       (promotion) =>
//         promotion?.promoType === "percentage_off" &&
//         Number(promotion?.discountPercent) === 50 &&
//         promotion?.scopeType === "global"
//     );
//   }, [activePromotions]);

//   const [timeLeft, setTimeLeft] = useState(null);

//   useEffect(() => {
//     if (!salePromotion?.endDate) {
//       setTimeLeft(null);
//       return;
//     }

//     const updateTimer = () => {
//       const now = Date.now();
//       const end = new Date(salePromotion.endDate).getTime();
//       const distance = end - now;

//       if (distance <= 0) {
//         setTimeLeft(null);
//         return;
//       }

//       setTimeLeft({
//         days: Math.floor(distance / (1000 * 60 * 60 * 24)),
//         hours: Math.floor((distance / (1000 * 60 * 60)) % 24),
//         minutes: Math.floor((distance / (1000 * 60)) % 60),
//         seconds: Math.floor((distance / 1000) % 60),
//       });
//     };

//     updateTimer();
//     const interval = window.setInterval(updateTimer, 1000);

//     return () => window.clearInterval(interval);
//   }, [salePromotion]);

//   if (!salePromotion) return null;

//   return (
//     <Box
//       sx={{
//         position: "relative",
//         width: "100%",
//         minHeight: { xs: 200, sm: 200, md: 200 },
//         borderRadius: "18px",
//         overflow: "hidden",
//         border: "1px solid rgba(255,255,255,0.2)",
//         backgroundImage: `
//           linear-gradient(180deg, rgba(4, 7, 12, 0.10) 0%, rgba(4, 7, 12, 0.12) 45%, rgba(4, 7, 12, 0.26) 100%),
//           linear-gradient(90deg, rgba(5, 8, 14, 0.78) 0%, rgba(5, 8, 14, 0.42) 52%, rgba(5, 8, 14, 0.16) 100%),
//           url("/assets/plugins-collage-banner.png")
//         `,
//         backgroundSize: "cover",
//         backgroundPosition: "center",
//         backgroundRepeat: "no-repeat",
//         boxShadow: "0 10px 28px rgba(0,0,0,0.28)",
//         isolation: "isolate",
//       }}
//     >
//       <Box
//         sx={{
//           position: "relative",
//           zIndex: 1,
//           px: { xs: 2, sm: 2.5, md: 3.25 },
//           py: { xs: 2, sm: 2.2, md: 2.1 },
//         }}
//       >
//         <Stack
//           direction={{ xs: "column", md: "row" }}
//           spacing={{ xs: 1, md: 1.5 }}
//           alignItems={{ xs: "stretch", md: "center" }}
//           justifyContent="space-between"
//         >
//           <Box sx={{ minWidth: 0, textAlign: { xs: "center", md: "left" } }}>
//             <Typography
//               sx={{
//                 color: "#ffcf63",
//                 fontWeight: 700,
//                 textTransform: "uppercase",
//                 letterSpacing: "0.22em",
//                 fontSize: { xs: "0.82rem", md: "0.9rem" },
//                 lineHeight: 1.1,
//                 mb: 0.75,
//               }}
//             >
//               Parish Audio
//             </Typography>

//             <Box
//               sx={{
//                 width: 90,
//                 height: 4,
//                 borderRadius: "999px",
//                 background: "linear-gradient(90deg, #ff9d2c 0%, #ffcf63 100%)",
//                 boxShadow: "0 0 20px rgba(255, 157, 44, 0.28)",
//                 mb: 1.6,
//                 mx: { xs: "auto", md: 0 },
//               }}
//             />

//             <Typography
//               sx={{
//                 color: "#f4f6f8",
//                 fontWeight: 800,
//                 lineHeight: 0.95,
//                 textTransform: "uppercase",
//                 letterSpacing: "-0.03em",
//                 fontSize: { xs: "2.1rem", sm: "2.6rem", md: "3rem" },
//                 textShadow: "0 10px 24px rgba(0, 0, 0, 0.35)",
//               }}
//             >
//               Summer Sale
//             </Typography>

//             <Typography
//               sx={{
//                 mt: 0.4,
//                 color: "#ffcf63",
//                 fontWeight: 900,
//                 lineHeight: 0.9,
//                 letterSpacing: "-0.05em",
//                 fontSize: { xs: "2.2rem", sm: "3rem", md: "3.75rem" },
//                 textTransform: "uppercase",
//                 textShadow:
//                   "0 0 28px rgba(255, 157, 44, 0.22), 0 12px 30px rgba(0, 0, 0, 0.36)",
//               }}
//             >
//               50% OFF
//             </Typography>

//             <Typography
//               sx={{
//                 mt: 0.35,
//                 color: "#f4f6f8",
//                 fontWeight: 700,
//                 letterSpacing: "0.18em",
//                 textTransform: "uppercase",
//                 fontSize: { xs: "1rem", sm: "1.1rem", md: "1.2rem" },
//               }}
//             >
//               Every Plugin
//             </Typography>

//             {!timeLeft && (
//               <Box
//                 sx={{
//                   display: "inline-flex",
//                   alignItems: "center",
//                   minHeight: 48,
//                   px: 2.1,
//                   mt: 1.6,
//                   borderRadius: "14px",
//                   background: "rgba(11, 16, 24, 0.78)",
//                   border: "1px solid rgba(255, 255, 255, 0.08)",
//                   backdropFilter: "blur(8px)",
//                   boxShadow: "0 8px 24px rgba(0, 0, 0, 0.25)",
//                 }}
//               >
//                 <Typography
//                   sx={{
//                     color: "#c9ced6",
//                     fontWeight: 600,
//                     fontSize: { xs: "1rem", md: "1.05rem" },
//                   }}
//                 >
//                   Through July 5
//                 </Typography>
//               </Box>
//             )}
//           </Box>

//           <Stack
//             spacing={1.25}
//             alignItems="center"
//             justifyContent="center"
//             sx={{
//               flexShrink: 0,
//               width: { xs: "100%", md: "auto" },
//             }}
//           >
//             {timeLeft && (
//               <Stack
//                 spacing={0.8}
//                 alignItems="center"
//                 justifyContent="center"
//               >
//                 <Typography
//                   sx={{
//                     color: "#ffcf63",
//                     fontWeight: 900,
//                     textTransform: "uppercase",
//                     letterSpacing: "0.08em",
//                     fontSize: { xs: "0.72rem", md: "0.8rem" },
//                     whiteSpace: "nowrap",
//                   }}
//                 >
//                   Sale Ends In
//                 </Typography>

//                 <Stack direction="row" spacing={0.75} justifyContent="center">
//                   <CountdownBox label="days" value={timeLeft.days} />
//                   <CountdownBox label="hrs" value={timeLeft.hours} />
//                   <CountdownBox label="min" value={timeLeft.minutes} />
//                   <CountdownBox label="sec" value={timeLeft.seconds} />
//                 </Stack>
//               </Stack>
//             )}

//             <Button
//               component={RouterLink}
//               to="/products"
//               sx={{
//                 borderRadius: "14px",
//                 px: 2.7,
//                 py: 1.1,
//                 textTransform: "uppercase",
//                 fontWeight: 800,
//                 letterSpacing: "0.04em",
//                 color: "#181109",
//                 background: "linear-gradient(180deg, #ffcf63 0%, #ff9d2c 100%)",
//                 border: "none",
//                 minWidth: { xs: 220, sm: 220 },
//                 boxShadow:
//                   "0 12px 30px rgba(0, 0, 0, 0.30), 0 0 22px rgba(255, 157, 44, 0.18)",
//                 "&:hover": {
//                   background:
//                     "linear-gradient(180deg, #ffd978 0%, #ffab3d 100%)",
//                   transform: "translateY(-1px)",
//                 },
//               }}
//             >
//               Shop the Sale
//             </Button>
//           </Stack>
//         </Stack>
//       </Box>
//     </Box>
//   );
// }

// function CountdownBox({ value, label }) {
//   const displayValue = String(value).padStart(2, "0");

//   return (
//     <Box
//       sx={{
//         minWidth: { xs: 54, sm: 60 },
//         px: 0.8,
//         py: 0.65,
//         borderRadius: "10px",
//         background: "linear-gradient(180deg, #fff7df 0%, #ffd166 100%)",
//         border: "1px solid rgba(255,255,255,0.35)",
//         boxShadow: "0 5px 14px rgba(0,0,0,0.22)",
//         textAlign: "center",
//       }}
//     >
//       <Typography
//         sx={{
//           color: "#111",
//           fontWeight: 900,
//           fontSize: { xs: "1rem", sm: "1.15rem" },
//           lineHeight: 1,
//         }}
//       >
//         {displayValue}
//       </Typography>

//       <Typography
//         sx={{
//           color: "rgba(0,0,0,0.68)",
//           fontWeight: 800,
//           fontSize: "0.68rem",
//           lineHeight: 1.15,
//           mt: 0.35,
//           textTransform: "uppercase",
//         }}
//       >
//         {label}
//       </Typography>
//     </Box>
//   );
// }


// // import React, { useEffect, useMemo, useState } from "react";
// // import { Box, Button, Stack, Typography } from "@mui/material";
// // import { Link as RouterLink } from "react-router-dom";
// // import { usePromotions } from "../../context/promotionsContext/promotionsContext";

// // export default function FlashSaleBanner() {
// //   const { activePromotions } = usePromotions();

// //   const salePromotion = useMemo(() => {
// //     return activePromotions.find(
// //       (promotion) =>
// //         promotion?.promoType === "percentage_off" &&
// //         Number(promotion?.discountPercent) === 40 &&
// //         promotion?.scopeType === "global"
// //     );
// //   }, [activePromotions]);

// //   const [timeLeft, setTimeLeft] = useState(null);

// //   useEffect(() => {
// //     if (!salePromotion?.endDate) return;

// //     const updateTimer = () => {
// //       const now = new Date().getTime();
// //       const end = new Date(salePromotion.endDate).getTime();
// //       const distance = end - now;

// //       if (distance <= 0) {
// //         setTimeLeft(null);
// //         return;
// //       }

// //       setTimeLeft({
// //         days: Math.floor(distance / (1000 * 60 * 60 * 24)),
// //         hours: Math.floor((distance / (1000 * 60 * 60)) % 24),
// //         minutes: Math.floor((distance / (1000 * 60)) % 60),
// //         seconds: Math.floor((distance / 1000) % 60),
// //       });
// //     };

// //     updateTimer();
// //     const interval = setInterval(updateTimer, 1000);

// //     return () => clearInterval(interval);
// //   }, [salePromotion]);

// //   if (!salePromotion) return null;

// //   return (
// //     <Box
// //       sx={{
// //         width: "100%",
// //         borderRadius: "18px",
// //         overflow: "hidden",
// //         border: "1px solid rgba(255, 180, 70, 0.65)",
// //         background: `
// //           radial-gradient(circle at 10% 20%, rgba(255, 204, 77, 0.38), transparent 34%),
// //           radial-gradient(circle at 90% 80%, rgba(255, 138, 0, 0.34), transparent 38%),
// //           linear-gradient(135deg, #22160a 0%, #111216 55%, #1c1208 100%)
// //         `,
// //         boxShadow: "0 10px 28px rgba(0,0,0,0.28)",
// //       }}
// //     >
// //       <Box
// //         sx={{
// //           px: { xs: 1.5, sm: 2.25, md: 3 },
// //           py: { xs: 1.35, md: 1.65 },
// //         }}
// //       >
// //         <Stack
// //           direction={{ xs: "column", md: "row" }}
// //           spacing={{ xs: 1.25, md: 2 }}
// //           alignItems="center"
// //           justifyContent="space-between"
// //           textAlign={{ xs: "center", md: "left" }}
// //         >
// //           <Box sx={{ minWidth: 0 }}>
// //             <Typography
// //               sx={{
// //                 color: "#ffcc4d",
// //                 fontWeight: 900,
// //                 textTransform: "uppercase",
// //                 letterSpacing: "0.1em",
// //                 fontSize: { xs: "0.78rem", md: "0.88rem" },
// //                 lineHeight: 1.1,
// //               }}
// //             >
// //               Spring Sale
// //             </Typography>

// //             <Typography
// //               sx={{
// //                 color: "#f7f7f7",
// //                 fontWeight: 900,
// //                 lineHeight: 1.05,
// //                 fontSize: { xs: "1.75rem", sm: "2.1rem", md: "2.45rem" },
// //                 letterSpacing: "-0.035em",
// //               }}
// //             >
// //               40% Off All Plugins
// //             </Typography>
// //           </Box>

// //           {timeLeft && (
// //             <Stack
// //               direction={{ xs: "column", sm: "row" }}
// //               spacing={0.8}
// //               alignItems="center"
// //               justifyContent="center"
// //               sx={{ flexShrink: 0 }}
// //             >
// //               <Typography
// //                 sx={{
// //                   color: "#ffcc4d",
// //                   fontWeight: 900,
// //                   textTransform: "uppercase",
// //                   letterSpacing: "0.08em",
// //                   fontSize: { xs: "0.72rem", md: "0.82rem" },
// //                   whiteSpace: "nowrap",
// //                 }}
// //               >
// //                 Offer Ends In:
// //               </Typography>

// //               <Stack direction="row" spacing={0.75} justifyContent="center">
// //                 <CountdownBox label="days" value={timeLeft.days} />
// //                 <CountdownBox label="hrs" value={timeLeft.hours} />
// //                 <CountdownBox label="min" value={timeLeft.minutes} />
// //                 <CountdownBox label="sec" value={timeLeft.seconds} />
// //               </Stack>
// //             </Stack>
// //           )}

// //           <Button
// //             component={RouterLink}
// //             to="/plugins"
// //             sx={{
// //               borderRadius: "6px",
// //               px: 2.4,
// //               py: 0.8,
// //               textTransform: "none",
// //               fontWeight: 900,
// //               color: "#111",
// //               backgroundColor: "#ffcc4d",
// //               border: "1px solid rgba(255,255,255,0.16)",
// //               minWidth: { xs: "100%", sm: 150 },
// //               "&:hover": {
// //                 backgroundColor: "#ffd766",
// //                 transform: "translateY(-1px)",
// //               },
// //             }}
// //           >
// //             Shop the Sale
// //           </Button>
// //         </Stack>
// //       </Box>
// //     </Box>
// //   );
// // }

// // function CountdownBox({ value, label }) {
// //   const displayValue = String(value).padStart(2, "0");

// //   return (
// //     <Box
// //       sx={{
// //         minWidth: { xs: 54, sm: 60 },
// //         px: 0.8,
// //         py: 0.65,
// //         borderRadius: "10px",
// //         background: "linear-gradient(180deg, #fff7df 0%, #ffd166 100%)",
// //         border: "1px solid rgba(255,255,255,0.35)",
// //         boxShadow: "0 5px 14px rgba(0,0,0,0.22)",
// //         textAlign: "center",
// //       }}
// //     >
// //       <Typography
// //         sx={{
// //           color: "#111",
// //           fontWeight: 900,
// //           fontSize: { xs: "1rem", sm: "1.15rem" },
// //           lineHeight: 1,
// //         }}
// //       >
// //         {displayValue}
// //       </Typography>

// //       <Typography
// //         sx={{
// //           color: "rgba(0,0,0,0.68)",
// //           fontWeight: 800,
// //           fontSize: "0.68rem",
// //           lineHeight: 1.15,
// //           mt: 0.35,
// //         }}
// //       >
// //         {label}
// //       </Typography>
// //     </Box>
// //   );
// // }